/**
 * The room is the content unit of ToyRaiders. It is plain, serialisable data:
 * a grid of props, one real chest, one door, and one sentence of trash talk.
 *
 * Everything downstream (builder, physics, replay, share link, future server)
 * consumes this and nothing else. If it isn't in `RoomDef`, it cannot travel
 * between two players.
 */

import type { PropTypeId } from './props'
import { propType } from './props'

export const ROOM_VERSION = 1 as const

/** Tunables. Reshape the game here, not in the systems. */
export const ROOM_CONFIG = {
  /** Interior grid, in cells. Small on purpose: a room must be readable in one glance on a phone. */
  width: 12,
  depth: 12,
  /** World units per cell. */
  cell: 1,
  /** Total prop cost the owner may spend. Scarcity is what makes bluffing worth it. */
  budget: 24,
  /** Hard cap on instances regardless of cost. */
  maxProps: 40,
  /** The chest must be a journey away from the door. */
  minChestDistance: 5,
  /** Cells directly in front of the door that must stay clear (so nobody is trapped at spawn). */
  doorClearance: 2,
  /** Seconds from grabbing the chest to the door locking. */
  escapeSeconds: 12,
  /** Total seconds a raid may last before the room throws you out. */
  raidSeconds: 90,
  maxTauntLength: 60,
  maxNameLength: 18,
} as const

/** Quarter turns, counter-clockwise, 0 = facing +Z (away from the door). */
export type Rot = 0 | 1 | 2 | 3

export interface PropInstance {
  /** Stable within a room; used by undo/redo and by replay events. */
  iid: number
  type: PropTypeId
  /** Integer cell coordinates, 0..width-1 / 0..depth-1. */
  x: number
  z: number
  rot: Rot
}

export interface Cell {
  x: number
  z: number
}

export interface RoomDef {
  v: typeof ROOM_VERSION
  /** Short id used in share links and (later) server rows. */
  id: string
  name: string
  ownerName: string
  /** The owner's one line of trash talk, shown to the raider before they enter. */
  taunt: string
  seed: number
  width: number
  depth: number
  props: PropInstance[]
  /** The real chest. */
  chest: Cell
  /** The door: where raiders enter and where they must return to escape. */
  door: Cell
  createdAt: number
}

/** Direction vector for a rotation, in cells. */
export function rotToDir(rot: Rot): { dx: number; dz: number } {
  switch (rot) {
    case 0:
      return { dx: 0, dz: 1 }
    case 1:
      return { dx: -1, dz: 0 }
    case 2:
      return { dx: 0, dz: -1 }
    case 3:
      return { dx: 1, dz: 0 }
  }
}

/** Cell centre → world position (room is centred on the origin). */
export function cellToWorld(
  x: number,
  z: number,
  width: number = ROOM_CONFIG.width,
  depth: number = ROOM_CONFIG.depth,
): { x: number; z: number } {
  const c = ROOM_CONFIG.cell
  return {
    x: (x - (width - 1) / 2) * c,
    z: (z - (depth - 1) / 2) * c,
  }
}

/** World position → nearest cell (may be out of bounds; callers clamp or reject). */
export function worldToCell(
  wx: number,
  wz: number,
  width: number = ROOM_CONFIG.width,
  depth: number = ROOM_CONFIG.depth,
): Cell {
  const c = ROOM_CONFIG.cell
  return {
    x: Math.round(wx / c + (width - 1) / 2),
    z: Math.round(wz / c + (depth - 1) / 2),
  }
}

export function cellKey(x: number, z: number): number {
  return z * 1000 + x
}

export function sameCell(a: Cell, b: Cell): boolean {
  return a.x === b.x && a.z === b.z
}

/** Manhattan distance in cells — the metric the legality rules speak in. */
export function cellDistance(a: Cell, b: Cell): number {
  return Math.abs(a.x - b.x) + Math.abs(a.z - b.z)
}

export function spentBudget(props: readonly PropInstance[]): number {
  let total = 0
  for (const p of props) total += propType(p.type).cost
  return total
}

export function countByType(props: readonly PropInstance[]): Map<PropTypeId, number> {
  const m = new Map<PropTypeId, number>()
  for (const p of props) m.set(p.type, (m.get(p.type) ?? 0) + 1)
  return m
}

/** The door sits centred on the z=0 wall. */
export function defaultDoor(width: number = ROOM_CONFIG.width): Cell {
  return { x: Math.floor(width / 2), z: 0 }
}

let idCounter = 0

/** Room ids are short and shareable, seeded so tests stay deterministic. */
export function makeRoomId(seed: number): string {
  const alphabet = '23456789abcdefghjkmnpqrstuvwxyz'
  let n = (seed ^ (idCounter++ << 17)) >>> 0
  let out = ''
  for (let i = 0; i < 7; i++) {
    out += alphabet[n % alphabet.length]
    n = Math.floor(n / alphabet.length) + 7919
  }
  return out
}

export function emptyRoom(seed: number, ownerName = '匿名屋主'): RoomDef {
  return {
    v: ROOM_VERSION,
    id: makeRoomId(seed),
    name: '我的玩具屋',
    ownerName,
    taunt: '',
    seed,
    width: ROOM_CONFIG.width,
    depth: ROOM_CONFIG.depth,
    props: [],
    chest: { x: Math.floor(ROOM_CONFIG.width / 2), z: ROOM_CONFIG.depth - 2 },
    door: defaultDoor(),
    createdAt: 0,
  }
}

export function cloneRoom(room: RoomDef): RoomDef {
  return {
    ...room,
    props: room.props.map((p) => ({ ...p })),
    chest: { ...room.chest },
    door: { ...room.door },
  }
}

/** Next free instance id for a room. */
export function nextIid(room: RoomDef): number {
  let max = 0
  for (const p of room.props) if (p.iid > max) max = p.iid
  return max + 1
}
